import type { StageDefinition } from "./types.js";

export const DEFAULT_STAGE_ID = "looping-street";

const BASELINE_DECK = Object.freeze({
  apple: 2, snake: 2, cloud: 2, thorn: 2, door: 2, candle: 2,
  wind: 2, mirror: 2, sink: 1, rise: 1, hush: 2,
});

export const STAGES: readonly StageDefinition[] = [
  {
    id: "quiet-window",
    name: "静かな《窓》",
    nameKey: "stage.quietWindow.name",
    shortName: "静かな窓",
    rule: "none",
    storyOnly: true,
    tagline: "注目したレーンだけがご主人へ届く",
    summary: "固有ルールを持たない、共有盤面と注目だけの比較用舞台。",
    ruleText: "舞台固有の処理はありません。置かれた夢と二人の注目だけが朝を変えます。",
    deck: BASELINE_DECK,
  },
  {
    id: "dew-greenhouse",
    name: "夜露の温室",
    nameKey: "stage.dewGreenhouse.name",
    shortName: "温室",
    rule: "cultivate",
    storyOnly: true,
    tagline: "注目された夢が、符号方向へ育つ",
    summary: "見つめた夢が育つ。よい夢も悪い夢も、注目するほど強くなる。",
    ruleText: "影響を受けた後、注目された各レーンで最も浅い値0以外の夢が、符号方向へ恒久的に1育ちます。同じレーンを二人で見ても成長は1回です。",
    deck: {
      apple: 2, snake: 2, greenhouse_sprout: 2, night_flower: 2, sleeping_moss: 2, thorn: 2,
      gardener_tend: 2, pruning_shears: 2, transplant: 2, soil_blanket: 2,
    },
  },
  {
    id: "mirror-station",
    name: "鏡の駅",
    nameKey: "stage.mirrorStation.name",
    shortName: "鏡の駅",
    rule: "mirror-wrap",
    storyOnly: true,
    tagline: "横端を越えた夢は、反転して戻る",
    summary: "端は出口ではない。夢は反転し、反対側のホームへ戻ってくる。",
    ruleText: "左右移動で盤面の端から出た夢は消えず、幸福度影響の符号を反転して同じ深度の反対端へ現れます。そこに夢があれば合体します。",
    deck: {
      mirror_passenger: 2, lost_pair: 2, mirror_gate_object: 2, candle: 2, station_fog: 2, crowded_platform: 2,
      transfer_guide: 3, mirror_reflection: 1, turnaround_train: 3, departure_bell: 1,
    },
  },
  {
    id: "dragon-mountain",
    name: "竜の待つ山",
    nameKey: "stage.dragonMountain.name",
    shortName: "竜の山",
    rule: "dragon-rescue",
    storyOnly: true,
    tagline: "持ち物と二人の注目で、傷ついた夢を手当てする",
    summary: "第二章専用の舞台。救急道具と共鳴によって、傷ついたドラゴンを救助する。",
    ruleText: "《救急道具》と《傷ついたドラゴン》が同じレーンにあり、二人が同時にそのレーンへ注目すると、幸福度評価前に《空を飛ぶドラゴン》へ変化します。救急道具は時間経過しません。",
    deck: {
      story_sturdy_rope: 2, story_return_map: 2, cloud: 2,
      story_hero_boots: 2, story_honey_ration: 2, story_distant_roar: 2,
      wind: 2, hush: 2, sink: 2, rise: 2,
    },
  },
  {
    id: "looping-street",
    name: "帰らずの散歩道",
    nameKey: "stage.loopingStreet.name",
    shortName: "散歩道",
    rule: "none",
    tagline: "繰り返すか、帰らせるか",
    summary: "戻ってくるボールと、夢を深みへ戻す札、朝へ送り出す札を使う基準舞台。",
    ruleText: "舞台の常時効果はありません。《赤いボール》《もう一周》《帰り道》など4枚の専用札が演出卓へ加わります。",
    deck: BASELINE_DECK,
  },
  {
    id: "dragon-adventure",
    name: "竜の待つ山",
    nameKey: "stage.dragonAdventure.name",
    shortName: "竜の山",
    rule: "none",
    tagline: "危険な夢を、力へ変える",
    summary: "傷ついたドラゴンと救急道具を合体させ、大きな幸福へ変える舞台。",
    ruleText: "舞台の常時効果はありません。《傷ついたドラゴン》と《救急道具》が合体すると、《空を飛ぶドラゴン》へ変わります。",
    deck: BASELINE_DECK,
  },
  {
    id: "fireside",
    name: "暖炉の前",
    nameKey: "stage.fireside.name",
    shortName: "暖炉",
    rule: "none",
    tagline: "渡す時を選び、夢を留める",
    summary: "雪や贈り物で時間を調整し、温かな夢と苦い夢の届く時を選ぶ舞台。",
    ruleText: "舞台の常時効果はありません。《降り積もる雪》《包み直した贈り物》など、時間をずらす専用札を使います。",
    deck: BASELINE_DECK,
  },
  {
    id: "winter-plaza",
    name: "冬の広場",
    nameKey: "stage.winterPlaza.name",
    shortName: "冬の広場",
    rule: "none",
    tagline: "注目を集め、大きな一手を打つ",
    summary: "二人の視線で鳴るピアノと、影響を倍にするアンコールを扱う舞台。",
    ruleText: "舞台の常時効果はありません。《ストリートピアノ》《アンコール》《時計の針を戻す》が演出卓へ加わります。",
    deck: BASELINE_DECK,
  },
  {
    id: "rain-study",
    name: "雨音の書斎",
    nameKey: "stage.rainStudy.name",
    shortName: "書斎",
    rule: "none",
    tagline: "残す、重ねる、手放す",
    summary: "場に残る記憶を複製し、ときには長い休暇へ送り出す舞台。",
    ruleText: "舞台の常時効果はありません。《積み重なる時間》で夢を複製し、《長い休暇》で盤面から除外できます。",
    deck: BASELINE_DECK,
  },
  {
    id: "layered-stage",
    name: "重なる舞台",
    nameKey: "stage.layeredStage.name",
    shortName: "重なる舞台",
    rule: "none",
    tagline: "合体と残響から、七つ目の音を作る",
    summary: "休符と和音で盤面を整え、レーンの影響を新しい音へ写し取る上級舞台。",
    ruleText: "舞台の常時効果はありません。《和音》で夢を合体させ、《七つ目の音》へ現在の影響を写し取ります。",
    deck: BASELINE_DECK,
  },
];

const stageMap = new Map(STAGES.map((stage) => [stage.id, stage]));

export function getStage(stageId: string | undefined): StageDefinition | null {
  return stageId ? stageMap.get(stageId) ?? null : null;
}
